import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { getStatusColor } from "@/utils/statusUtils";


interface OvertimeRecord {
  id: string;
  date: string;
  hours: number;
  justification: string;
  status: string;
  startTime?: string | null;
  endTime?: string | null;
  approverName?: string | null;
  approverComment?: string | null;
  submittedAt: string;
  updatedAt?: string | null;
  shiftAssignmentId?: string | null;
  shiftId?: string | null;
}

// Define props for OvertimeList
interface OvertimeListProps {
  employeeId?: string;
  startDate?: Date;
  endDate?: Date;
  searchQuery?: string;
}

function OvertimeListComponent({ employeeId, startDate, endDate, searchQuery }: OvertimeListProps) {
  const [overtimeHistory, setOvertimeHistory] = useState<OvertimeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredHistory, setFilteredHistory] = useState<OvertimeRecord[]>([]);
  
  // Memoize the fetchOvertimeData function to prevent unnecessary recreations
  const fetchOvertimeData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      let query = supabase
        .from('overtime_requests')
        .select(`
          id,
          employee_id,
          employee_name,
          department,
          date,
          hours,
          justification,
          start_time,
          end_time,
          status,
          submitted_at,
          updated_at,
          approver_name,
          approver_comment,
          shift_assignment_id,
          shift_id
        `);
        
      // If employeeId is provided, filter by that employee
      if (employeeId) {
        query = query.eq('employee_id', employeeId);
      }

      // Filter by date range if provided
      if (startDate) {
        query = query.gte('date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        query = query.lte('date', format(endDate, 'yyyy-MM-dd'));
      }
      
      // Order by date
      query = query.order('date', { ascending: false });

      const { data, error } = await query;
      
      if (error) throw error;
      
      const mappedData = data?.map((record: Record<string, unknown>) => ({
        id: String(record.id),
        date: String(record.date),
        hours: Number(record.hours),
        justification: String(record.justification),
        status: String(record.status),
        startTime: record.start_time ? String(record.start_time) : null,
        endTime: record.end_time ? String(record.end_time) : null,
        approverName: record.approver_name ? String(record.approver_name) : null,
        approverComment: record.approver_comment ? String(record.approver_comment) : null,
        submittedAt: String(record.submitted_at),
        updatedAt: record.updated_at ? String(record.updated_at) : null,
        shiftAssignmentId: record.shift_assignment_id ? String(record.shift_assignment_id) : null,
        shiftId: record.shift_id ? String(record.shift_id) : null
      })) || [];
      setOvertimeHistory(mappedData);
      setFilteredHistory(mappedData);
    } catch {
      // Error handled silently
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, startDate, endDate]);

  // Setup data fetching - REAL-TIME SUBSCRIPTION DISABLED to prevent infinite loops
  useEffect(() => {
    // Fetch initial data only
    fetchOvertimeData();
    
    // DISABLED: Real-time subscription removed to prevent infinite loops
    // Instead, data will refresh when the component mounts or when manually triggered
    
    // No cleanup needed since we're not creating a subscription
  }, [fetchOvertimeData, employeeId]);

  // Apply search filter when searchQuery or fetched data changes
  // Use useMemo to optimize filtering operation
  useMemo(() => {
    if (!searchQuery) {
      setFilteredHistory(overtimeHistory); // If no search query, show all
      return;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    const filtered = overtimeHistory.filter(record => 
      record.justification.toLowerCase().includes(lowerCaseQuery) ||
      record.status.toLowerCase().includes(lowerCaseQuery) ||
      (record.approverName || '').toLowerCase().includes(lowerCaseQuery) ||
      (record.approverComment || '').toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredHistory(filtered);
  }, [searchQuery, overtimeHistory]);

  // Memoize the date formatting function
  const formatDate = useCallback((dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  }, []);


  if (isLoading) {
    return <div>Loading overtime history...</div>;
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Hours</TableHead>
            <TableHead>Justification</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Approver</TableHead>
            <TableHead>Comments</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredHistory.map((record) => (
            <TableRow key={record.id}>
              <TableCell>{formatDate(record.date)}</TableCell>
              <TableCell>{record.hours}</TableCell>
              <TableCell>{record.justification}</TableCell>
              <TableCell>
                <span className={getStatusColor(record.status, 'light')}>
                  {record.status}
                </span>
              </TableCell>
              <TableCell>{record.approverName || '-'}</TableCell>
              <TableCell>{record.approverComment || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Export with React.memo to prevent unnecessary re-renders
export const OvertimeList = React.memo(OvertimeListComponent);
